// QueueManagement.js - Queue Configuration Management UI
import React, { useState, useEffect } from 'react';
import apiService from './apiService';

const QueueManagement = ({ theme, language }) => {
  const [queues, setQueues] = useState([]);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const [queueConfig, setQueueConfig] = useState(null);
  const [audioFiles, setAudioFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState('general');

  const t = {
    uk: {
      title: 'Управління чергами',
      selectQueue: 'Виберіть чергу',
      general: 'Загальні',
      behavior: 'Поведінка',
      audio: 'Аудіо',
      save: 'Зберегти',
      cancel: 'Скасувати',
      upload: 'Завантажити',
      delete: 'Видалити',
      generate: 'Згенерувати конфігурацію',
      loading: 'Завантаження...',
      displayName: 'Назва для відображення',
      strategy: 'Стратегія',
      timeout: 'Таймаут (сек)',
      retry: 'Повтор (сек)',
      wrapuptime: 'Час завершення (сек)',
      maxlen: 'Макс. довжина черги',
      musicOnHold: 'Музика на утриманні',
      musicClass: 'Клас музики',
      announcements: 'Оголошення',
      uploadMOH: 'Завантажити музику',
      uploadAnnouncement: 'Завантажити оголошення',
      currentFiles: 'Поточні файли',
      noFiles: 'Немає файлів',
      success: 'Успішно збережено',
      error: 'Помилка',
      confirmDelete: 'Видалити цей файл?',
      generateSuccess: 'Конфігурацію згенеровано та перезавантажено',
      strategies: {
        ringall: 'Дзвонити всім',
        rrmemory: 'Циклічно (з пам\'яттю)',
        leastrecent: 'Найдавніший',
        fewestcalls: 'Найменше дзвінків',
        random: 'Випадково'
      }
    },
    en: {
      title: 'Queue Management',
      selectQueue: 'Select Queue',
      general: 'General',
      behavior: 'Behavior',
      audio: 'Audio',
      save: 'Save',
      cancel: 'Cancel',
      upload: 'Upload',
      delete: 'Delete',
      generate: 'Generate Configuration',
      loading: 'Loading...',
      displayName: 'Display Name',
      strategy: 'Strategy',
      timeout: 'Timeout (sec)',
      retry: 'Retry (sec)',
      wrapuptime: 'Wrap-up Time (sec)',
      maxlen: 'Max Queue Length',
      musicOnHold: 'Music on Hold',
      musicClass: 'Music Class',
      announcements: 'Announcements',
      uploadMOH: 'Upload Music',
      uploadAnnouncement: 'Upload Announcement',
      currentFiles: 'Current Files',
      noFiles: 'No files',
      success: 'Successfully saved',
      error: 'Error',
      confirmDelete: 'Delete this file?',
      generateSuccess: 'Configuration generated and reloaded',
      strategies: {
        ringall: 'Ring All',
        rrmemory: 'Round Robin (Memory)',
        leastrecent: 'Least Recent',
        fewestcalls: 'Fewest Calls',
        random: 'Random'
      }
    }
  };

  const text = t[language] || t.en;

  useEffect(() => {
    loadQueues();
  }, []);

  const loadQueues = async () => {
    try {
      setLoading(true);
      const data = await apiService.getQueueConfigs();
      setQueues(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadQueueConfig = async (queueName) => {
    try {
      setLoading(true);
      const data = await apiService.getQueueConfig(queueName);
      setQueueConfig(data.config);
      setAudioFiles(data.audioFiles);
      setSelectedQueue(queueName);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      await apiService.updateQueueConfig(selectedQueue, queueConfig);
      setSuccess(text.success);
      setTimeout(() => setSuccess(null), 3000);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (fileType, event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setLoading(true);
      await apiService.uploadQueueAudio(selectedQueue, fileType, file);
      await loadQueueConfig(selectedQueue);
      setSuccess(`${fileType === 'moh' ? text.musicOnHold : text.announcements} ${text.success}`);
      setTimeout(() => setSuccess(null), 3000);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteFile = async (audioId) => {
    if (!window.confirm(text.confirmDelete)) return;

    try {
      setLoading(true);
      await apiService.deleteQueueAudio(audioId);
      await loadQueueConfig(selectedQueue);
      setSuccess(text.success);
      setTimeout(() => setSuccess(null), 3000);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateConfig = async () => {
    try {
      setLoading(true);
      await apiService.generateQueueConfig();
      setSuccess(text.generateSuccess);
      setTimeout(() => setSuccess(null), 3000);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const updateConfig = (field, value) => {
    setQueueConfig({ ...queueConfig, [field]: value });
  };

  return (
    <div style={{
      padding: '20px',
      backgroundColor: theme === 'dark' ? '#1e1e1e' : '#ffffff',
      color: theme === 'dark' ? '#e0e0e0' : '#333333',
      minHeight: '100vh'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h2>{text.title}</h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={async () => {
              try {
                setLoading(true);
                const result = await apiService.syncQueuesFromAsterisk();
                setSuccess(`${language === 'uk' ? 'Синхронізовано' : 'Synced'} ${result.queues?.length || 0} ${language === 'uk' ? 'черг' : 'queues'}`);
                setTimeout(() => setSuccess(null), 3000);
                loadQueues(); // Reload queue list
                setError(null);
              } catch (err) {
                setError(err.message);
              } finally {
                setLoading(false);
              }
            }}
            disabled={loading}
            style={{
              padding: '10px 20px',
              backgroundColor: '#2196F3',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1
            }}
          >
            {language === 'uk' ? 'Синхронізувати з Asterisk' : 'Sync from Asterisk'}
          </button>
          <button
            onClick={handleGenerateConfig}
            disabled={loading}
            style={{
              padding: '10px 20px',
              backgroundColor: '#4CAF50',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.6 : 1
            }}
          >
            {text.generate}
          </button>
        </div>
      </div>

      {error && (
        <div style={{
          padding: '10px',
          backgroundColor: '#f44336',
          color: 'white',
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          {text.error}: {error}
        </div>
      )}

      {success && (
        <div style={{
          padding: '10px',
          backgroundColor: '#4CAF50',
          color: 'white',
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          {success}
        </div>
      )}

      <div style={{ display: 'flex', gap: '20px' }}>
        {/* Queue List */}
        <div style={{
          width: '250px',
          backgroundColor: theme === 'dark' ? '#2d2d2d' : '#f5f5f5',
          padding: '15px',
          borderRadius: '8px'
        }}>
          <h3>{text.selectQueue}</h3>
          {loading && !selectedQueue ? (
            <div>{text.loading}</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {queues.map(queue => (
                <button
                  key={queue.queue_name}
                  onClick={() => loadQueueConfig(queue.queue_name)}
                  style={{
                    padding: '10px',
                    backgroundColor: selectedQueue === queue.queue_name
                      ? (theme === 'dark' ? '#4CAF50' : '#4CAF50')
                      : (theme === 'dark' ? '#3d3d3d' : '#ffffff'),
                    color: selectedQueue === queue.queue_name ? 'white' : (theme === 'dark' ? '#e0e0e0' : '#333'),
                    border: '1px solid ' + (theme === 'dark' ? '#555' : '#ddd'),
                    borderRadius: '4px',
                    cursor: 'pointer',
                    textAlign: 'left'
                  }}
                >
                  {queue.display_name || queue.queue_name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Configuration Panel */}
        {queueConfig && (
          <div style={{ flex: 1 }}>
            {/* Tabs */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', borderBottom: '2px solid ' + (theme === 'dark' ? '#555' : '#ddd') }}>
              {['general', 'behavior', 'audio'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: activeTab === tab ? (theme === 'dark' ? '#4CAF50' : '#4CAF50') : 'transparent',
                    color: activeTab === tab ? 'white' : (theme === 'dark' ? '#e0e0e0' : '#333'),
                    border: 'none',
                    borderBottom: activeTab === tab ? '2px solid #4CAF50' : 'none',
                    cursor: 'pointer',
                    fontWeight: activeTab === tab ? 'bold' : 'normal'
                  }}
                >
                  {text[tab]}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div style={{
              backgroundColor: theme === 'dark' ? '#2d2d2d' : '#f5f5f5',
              padding: '20px',
              borderRadius: '8px'
            }}>
              {activeTab === 'general' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px' }}>{text.displayName}</label>
                    <input
                      type="text"
                      value={queueConfig.display_name || ''}
                      onChange={(e) => updateConfig('display_name', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px',
                        backgroundColor: theme === 'dark' ? '#3d3d3d' : '#ffffff',
                        color: theme === 'dark' ? '#e0e0e0' : '#333',
                        border: '1px solid ' + (theme === 'dark' ? '#555' : '#ddd'),
                        borderRadius: '4px'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '5px' }}>{text.strategy}</label>
                    <select
                      value={queueConfig.strategy || 'ringall'}
                      onChange={(e) => updateConfig('strategy', e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px',
                        backgroundColor: theme === 'dark' ? '#3d3d3d' : '#ffffff',
                        color: theme === 'dark' ? '#e0e0e0' : '#333',
                        border: '1px solid ' + (theme === 'dark' ? '#555' : '#ddd'),
                        borderRadius: '4px'
                      }}
                    >
                      {Object.entries(text.strategies).map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </select>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px' }}>{text.timeout}</label>
                      <input
                        type="number"
                        value={queueConfig.timeout || 30}
                        onChange={(e) => updateConfig('timeout', parseInt(e.target.value))}
                        style={{
                          width: '100%',
                          padding: '8px',
                          backgroundColor: theme === 'dark' ? '#3d3d3d' : '#ffffff',
                          color: theme === 'dark' ? '#e0e0e0' : '#333',
                          border: '1px solid ' + (theme === 'dark' ? '#555' : '#ddd'),
                          borderRadius: '4px'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '5px' }}>{text.retry}</label>
                      <input
                        type="number"
                        value={queueConfig.retry || 5}
                        onChange={(e) => updateConfig('retry', parseInt(e.target.value))}
                        style={{
                          width: '100%',
                          padding: '8px',
                          backgroundColor: theme === 'dark' ? '#3d3d3d' : '#ffffff',
                          color: theme === 'dark' ? '#e0e0e0' : '#333',
                          border: '1px solid ' + (theme === 'dark' ? '#555' : '#ddd'),
                          borderRadius: '4px'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '5px' }}>{text.wrapuptime}</label>
                      <input
                        type="number"
                        value={queueConfig.wrapuptime || 0}
                        onChange={(e) => updateConfig('wrapuptime', parseInt(e.target.value))}
                        style={{
                          width: '100%',
                          padding: '8px',
                          backgroundColor: theme === 'dark' ? '#3d3d3d' : '#ffffff',
                          color: theme === 'dark' ? '#e0e0e0' : '#333',
                          border: '1px solid ' + (theme === 'dark' ? '#555' : '#ddd'),
                          borderRadius: '4px'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: '5px' }}>{text.maxlen}</label>
                      <input
                        type="number"
                        value={queueConfig.maxlen || 0}
                        onChange={(e) => updateConfig('maxlen', parseInt(e.target.value))}
                        style={{
                          width: '100%',
                          padding: '8px',
                          backgroundColor: theme === 'dark' ? '#3d3d3d' : '#ffffff',
                          color: theme === 'dark' ? '#e0e0e0' : '#333',
                          border: '1px solid ' + (theme === 'dark' ? '#555' : '#ddd'),
                          borderRadius: '4px'
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'behavior' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                  {['autofill', 'autopause', 'ringinuse', 'timeoutrestart'].map(field => (
                    <div key={field} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <input
                        type="checkbox"
                        checked={queueConfig[field] || false}
                        onChange={(e) => updateConfig(field, e.target.checked)}
                      />
                      <label>{field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}</label>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === 'audio' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  <div>
                    <h4>{text.musicOnHold}</h4>
                    
                    {/* Music Class Selector */}
                    <div style={{ marginBottom: '15px' }}>
                      <label style={{ display: 'block', marginBottom: '5px' }}>{text.musicClass}</label>
                      <select
                        value={queueConfig.musicclass || 'call-center'}
                        onChange={(e) => updateConfig('musicclass', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '8px',
                          backgroundColor: theme === 'dark' ? '#3d3d3d' : '#ffffff',
                          color: theme === 'dark' ? '#e0e0e0' : '#333',
                          border: '1px solid ' + (theme === 'dark' ? '#555' : '#ddd'),
                          borderRadius: '4px'
                        }}
                      >
                        <option value="call-center">Call Center</option>
                        <option value="office">Office</option>
                        <option value="default">Default</option>
                      </select>
                    </div>

                    {/* Upload Custom MOH */}
                    <div>
                      <label style={{ display: 'block', marginBottom: '5px' }}>{text.uploadMOH}</label>
                      <input
                        type="file"
                        accept="audio/wav,audio/mp3,audio/mpeg"
                        onChange={(e) => handleFileUpload('moh', e)}
                        style={{ marginBottom: '10px' }}
                      />
                    </div>
                    <div>
                      <strong>{text.currentFiles}:</strong>
                      {audioFiles.filter(f => f.file_type === 'moh').length === 0 ? (
                        <div style={{ marginTop: '10px', color: '#999' }}>{text.noFiles}</div>
                      ) : (
                        <div style={{ marginTop: '10px' }}>
                          {audioFiles.filter(f => f.file_type === 'moh').map(file => (
                            <div key={file.id} style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '10px',
                              backgroundColor: theme === 'dark' ? '#3d3d3d' : '#ffffff',
                              borderRadius: '4px',
                              marginBottom: '5px'
                            }}>
                              <span>{file.original_filename}</span>
                              <button
                                onClick={() => handleDeleteFile(file.id)}
                                style={{
                                  padding: '5px 10px',
                                  backgroundColor: '#f44336',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer'
                                }}
                              >
                                {text.delete}
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div>
                    <h4>{text.announcements}</h4>
                    <input
                      type="file"
                      accept="audio/wav,audio/mp3,audio/mpeg"
                      onChange={(e) => handleFileUpload('announcement', e)}
                      style={{ marginBottom: '10px' }}
                    />
                    <div>
                      <strong>{text.currentFiles}:</strong>
                      {audioFiles.filter(f => f.file_type === 'announcement').length === 0 ? (
                        <div style={{ marginTop: '10px', color: '#999' }}>{text.noFiles}</div>
                      ) : (
                        <div style={{ marginTop: '10px' }}>
                          {audioFiles.filter(f => f.file_type === 'announcement').map(file => (
                            <div key={file.id} style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                              padding: '10px',
                              backgroundColor: theme === 'dark' ? '#3d3d3d' : '#ffffff',
                              borderRadius: '4px',
                              marginBottom: '5px'
                            }}>
                              <span>{file.original_filename}</span>
                              <button
                                onClick={() => handleDeleteFile(file.id)}
                                style={{
                                  padding: '5px 10px',
                                  backgroundColor: '#f44336',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer'
                                }}
                              >
                                {text.delete}
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Save Button */}
              <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
                <button
                  onClick={handleSave}
                  disabled={loading}
                  style={{
                    padding: '10px 30px',
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.6 : 1
                  }}
                >
                  {loading ? text.loading : text.save}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QueueManagement;
